import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../../auth/AuthContext';
import type { Question } from '../../config/types';
import {
  type ListPageEditorFileResult,
  readListPageEditorFile,
  importListPageWorkbooks,
} from '../../utils/list-page-excel/editor-file';
import { getListPageEditorRows } from '../../utils/list-page-excel/editor-model';
import {
  downloadExcelBuffer,
  downloadListPageWorkbook,
  type ListPageExcelOptions,
} from '../../utils/list-page-excel/workbook';
import ExcelToolbar from './bulk-table/ExcelToolbar';
import InputMethodButton from './bulk-table/InputMethodButton';
import ListCompletionModeModal, {
  type ListCompletionMode,
} from './bulk-table/ListCompletionModeModal';
import ListPageExcelEditorModal from './bulk-table/ListPageExcelEditorModal';
import ExcelImportToolbar from './ExcelImportToolbar';
import PsfaCdrImport from './PsfaCdrImport';

const InputMethodPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
  padding: 14px 16px;
  border-left: 6px solid #4c2c92;
  background: #f4f0f7;
  box-shadow: inset 0 -1px 0 #d9cfe2;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;

    button {
      width: 100%;
    }
  }
`;

const InputMethodStatus = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
`;

const InputMethodLabel = styled.span`
  color: #505a5f;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
`;

const InputMethodValue = styled.strong`
  color: #0b0c0c;
  font-size: 1.1875rem;
  line-height: 1.3;
`;

const readCompletionMode = (key: string): ListCompletionMode | undefined => {
  try {
    const value = window.localStorage.getItem(key);
    return value === 'table' || value === 'web' ? value : undefined;
  } catch (error) {
    console.warn('Unable to read list completion preference.', error);
    return undefined;
  }
};

type Props = ListPageExcelOptions & {
  disabled?: boolean;
  confirmImport?: boolean;
  getAnswers?: () => Record<string, unknown>;
  ready?: boolean;
  autoOpenSelector?: boolean;
  autoOpenTable?: boolean;
  mode?: 'direct' | 'table-editor';
  onImport: (answers: Record<string, unknown>) => void | Promise<void>;
  onSaveUnfinished?: (answers: Record<string, unknown>) => void | Promise<void>;
};

export default function ListPageExcelToolbar({
  title,
  pageName,
  questions,
  answers,
  tenant,
  disabled = false,
  confirmImport = true,
  getAnswers,
  ready = true,
  autoOpenSelector = true,
  autoOpenTable = true,
  mode = 'direct',
  onImport,
  onSaveUnfinished,
}: Props) {
  const user = useContext(AuthContext)?.user;
  const preferenceKey =
    tenant === 'psfa' && user?.userId
      ? `grid-forms:${tenant}:list-completion-mode:${user.userId}`
      : undefined;
  const usesCompletionModeSelector = mode === 'table-editor' && tenant === 'psfa';
  const options = useMemo<ListPageExcelOptions>(
    () => ({
      title,
      pageName,
      questions: questions as Question[],
      answers,
      tenant,
    }),
    [answers, pageName, questions, tenant, title],
  );
  const getCurrentOptions = (): ListPageExcelOptions =>
    getAnswers ? { ...options, answers: getAnswers() } : options;
  const [editorRows, setEditorRows] = useState<unknown[][]>();
  const [editorMessage, setEditorMessage] = useState<string>();
  const [error, setError] = useState('');
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [cdrFiles, setCdrFiles] = useState<File[]>();
  const [completionMode, setCompletionMode] = useState<ListCompletionMode>();
  const [isCompletionModeReady, setIsCompletionModeReady] = useState(false);
  const [isCompletionModeOpen, setIsCompletionModeOpen] = useState(false);
  const hasAutoOpenedEditor = useRef(false);

  const openCurrentRows = () => {
    setError('');
    setEditorMessage(undefined);
    setEditorRows(getListPageEditorRows(options));
  };

  useEffect(() => {
    hasAutoOpenedEditor.current = false;
    if (!usesCompletionModeSelector) {
      setIsCompletionModeReady(true);
      return;
    }
    if (!preferenceKey) {
      setIsCompletionModeReady(false);
      return;
    }
    const shouldResetPreference = new URLSearchParams(window.location.search).get('reset') === '1';
    if (shouldResetPreference) {
      try {
        window.localStorage.removeItem(preferenceKey);
      } catch (error) {
        console.warn('Unable to reset list completion preference.', error);
      }
    }
    const storedMode = shouldResetPreference ? undefined : readCompletionMode(preferenceKey);
    setCompletionMode(storedMode);
    if (storedMode !== undefined) {
      setIsCompletionModeOpen(false);
    } else if (autoOpenSelector) {
      setIsCompletionModeOpen(true);
    }
    setIsCompletionModeReady(true);
  }, [autoOpenSelector, preferenceKey, usesCompletionModeSelector]);

  useEffect(() => {
    if (
      !usesCompletionModeSelector ||
      !isCompletionModeReady ||
      !ready ||
      !autoOpenTable ||
      completionMode !== 'table' ||
      hasAutoOpenedEditor.current
    ) {
      return;
    }
    hasAutoOpenedEditor.current = true;
    openCurrentRows();
  }, [autoOpenTable, completionMode, isCompletionModeReady, ready, usesCompletionModeSelector]);

  const chooseCompletionMode = (selectedMode: ListCompletionMode) => {
    if (preferenceKey) {
      try {
        window.localStorage.setItem(preferenceKey, selectedMode);
      } catch (storageError) {
        console.warn('Unable to save list completion preference.', storageError);
      }
    }
    setCompletionMode(selectedMode);
    setIsCompletionModeOpen(false);
    if (selectedMode === 'table' && ready) {
      hasAutoOpenedEditor.current = true;
      openCurrentRows();
    } else {
      setEditorRows(undefined);
    }
  };

  if (mode === 'direct') {
    return (
      <ExcelImportToolbar
        title={title}
        disabled={disabled}
        confirmImport={confirmImport}
        showImportErrorsInModal={tenant === 'secondments'}
        onDownload={() => downloadListPageWorkbook(getCurrentOptions())}
        readFiles={async (files) => {
          const result = await importListPageWorkbooks(files, getCurrentOptions());
          if (result.status === 'valid') {
            return { status: 'ready', values: result.answers };
          }
          if (result.status === 'invalid-template') {
            return { status: 'invalid-template' };
          }
          return {
            status: 'rejected',
            message: result.message,
            downloadRejectedFile: () =>
              downloadExcelBuffer(result.errorBuffer, result.errorFileName),
          };
        }}
        onImport={onImport}
      />
    );
  }

  const readEditorFiles = async (files: File[]): Promise<ListPageEditorFileResult> => {
    const rows: unknown[][] = [];
    const messages: string[] = [];
    let cdrCount = 0;
    for (const file of files) {
      const result = await readListPageEditorFile(file, getCurrentOptions());
      if (result.status === 'invalid-template') {
        if (tenant === 'psfa') {
          const ExcelJS = await import('exceljs');
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(await file.arrayBuffer());
          if (workbook.getWorksheet('Proforma') || workbook.getWorksheet('Organisation details')) {
            cdrCount += 1;
            continue;
          }
        }
        return result;
      }
      if (result.status === 'ready') {
        rows.push(...result.rows);
        if (result.message) messages.push(`${file.name}: ${result.message}`);
      }
    }
    if (cdrCount) {
      if (cdrCount !== files.length)
        throw new Error('Upload full CDR workbooks separately from section templates.');
      setCdrFiles(files);
      return { status: 'handled' };
    }
    return { status: 'ready', rows, message: messages.join(' ') || undefined };
  };

  const openUploadedFiles = async (files: File[]) => {
    setIsLoadingFile(true);
    setError('');
    try {
      const result = await readEditorFiles(files);
      if (result.status === 'handled') return;
      if (result.status === 'invalid-template') {
        setError('The template you uploaded is out of date or has been modified.');
        return;
      }
      setEditorRows(result.rows);
      setEditorMessage(result.message);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error ? uploadError.message : 'The XLSX file could not be imported.',
      );
    } finally {
      setIsLoadingFile(false);
    }
  };

  return (
    <>
      {usesCompletionModeSelector && isCompletionModeReady && (
        <InputMethodPanel>
          <InputMethodStatus>
            <InputMethodLabel>Input method</InputMethodLabel>
            <InputMethodValue>
              {completionMode === 'table'
                ? 'Table format'
                : completionMode === 'web'
                  ? 'Web form'
                  : 'Not selected'}
            </InputMethodValue>
          </InputMethodStatus>
          <InputMethodButton onClick={() => setIsCompletionModeOpen(true)} />
        </InputMethodPanel>
      )}
      <ExcelToolbar
        title={title}
        disabled={disabled || isLoadingFile}
        onDownload={openCurrentRows}
        onFiles={openUploadedFiles}
      />
      {error && (
        <p className="govuk-error-message govuk-!-margin-bottom-4" role="alert">
          <span className="govuk-visually-hidden">Error:</span> {error}
        </p>
      )}
      {editorRows && (
        <ListPageExcelEditorModal
          options={options}
          initialRows={editorRows}
          initialMessage={editorMessage}
          disabled={disabled}
          onClose={() => setEditorRows(undefined)}
          onChangeInputMethod={
            usesCompletionModeSelector ? () => setIsCompletionModeOpen(true) : undefined
          }
          onSave={onImport}
          onSaveUnfinished={onSaveUnfinished}
          readFiles={readEditorFiles}
        />
      )}
      {isCompletionModeOpen && (
        <ListCompletionModeModal
          currentMode={completionMode}
          onCancel={() => setIsCompletionModeOpen(false)}
          onContinue={chooseCompletionMode}
        />
      )}
      {cdrFiles && (
        <PsfaCdrImport
          files={cdrFiles}
          onClose={() => setCdrFiles(undefined)}
          onImported={() => {
            setEditorRows(undefined);
            setCdrFiles(undefined);
          }}
        />
      )}
    </>
  );
}
